import React, { useState, useEffect } from 'react';
import { api, downloadCSV, Badge, Pagination, paginate, SearchBox, searchFilter } from './shared';

const PlayersTab = ({ onError, canManage = false }) => {
  const [players, setPlayers] = useState(null);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');

  const fetch = async () => { setLoading(true); try { const r = await api('players'); setPlayers(r.data); setPage(1); } catch(e) { onError(e.message); } finally { setLoading(false); } };
  useEffect(() => { fetch(); }, []); // eslint-disable-line

  // build allPlayers (deduplicated by player_id) — รวม register-only newMembers ที่ยังไม่ active ใน BTS
  const allPlayers = (() => {
    if (!players) return [];
    const seen = new Set();
    const arr = [];
    const push = (p) => { if (!seen.has(p.player_id)) { seen.add(p.player_id); arr.push(p); } };
    players.inTeam.forEach(p => push({ ...p, playerType: parseInt(p.receipt_count || p.receiptCount || 0) > 0 ? 'inTeam' : 'inTeamNoScan', receiptCount: parseInt(p.receipt_count || p.receiptCount || 0), pendingCount: parseInt(p.pending_count || p.pendingCount || 0) }));
    players.waitingForTeam.forEach(p => push({ ...p, playerType: 'waiting', party_name: '-', personal_tickets: 0, member_count: 0, is_owner: false, is_new_member: false, receiptCount: 0, pendingCount: parseInt(p.pending_count || 0), joined_at: p.last_scanned_at || null }));
    (players.receiptOnly || []).forEach(p => push({ ...p, playerType: 'receiptOnly', party_name: '-', personal_tickets: 0, member_count: 0, is_owner: false, is_new_member: false, receiptCount: parseInt(p.receipt_count || 0), pendingCount: 0, joined_at: p.last_scanned_at || null }));
    // เพิ่ม register-only ที่ยังไม่อยู่ใน 3 ชุดข้างบน (ผูก BigPoint แต่ยังไม่ทำ activity)
    (players.newMembers || []).forEach(p => push({
      ...p,
      playerType: 'registerOnly',
      receiptCount: parseInt(p.receipt_count || 0),
      pendingCount: parseInt(p.pending_count || 0),
      party_name: p.party_name || '-',
      personal_tickets: p.personal_tickets || 0,
      is_owner: !!p.is_owner,
      is_new_member: !!p.is_new_member,
      joined_at: p.bigpoint_linked_at,
    }));
    return arr;
  })();
  // newMember = is_new_member=true (ได้ x2 bonus) — ใช้ allPlayers ครอบคลุมทุกคน ไม่จำกัดวันผูก BigPoint
  // match กับ "New Member" รวมใน DailyTab (= COUNT bts_party_members WHERE is_new_member=true)
  const newMemberPlayers = allPlayers.filter(p => !!p.is_new_member).map(p => ({
    ...p, playerType: 'newMember',
  }));
  const byType = filter === 'all' ? allPlayers :
    filter === 'newMember' ? newMemberPlayers :
    filter === 'inTeam' ? allPlayers.filter(p => p.playerType === 'inTeam' || p.playerType === 'receiptOnly') :
    filter === 'inTeamNoScan' ? allPlayers.filter(p => p.playerType === 'inTeamNoScan') :
    allPlayers.filter(p => p.playerType === 'waiting');
  const filtered = searchFilter(byType, search, ['display_name', 'bigpoint_id', 'party_name']);
  const summary = players ? {
    total: allPlayers.length,
    inTeam: allPlayers.filter(p => p.playerType === 'inTeam' || p.playerType === 'receiptOnly').length,
    inTeamNoScan: allPlayers.filter(p => p.playerType === 'inTeamNoScan').length,
    waiting: players.waitingForTeam.length,
    newMember: newMemberPlayers.length,
  } : null;

  return (
    <div className="bts-admin__section">
      <div className="bts-admin__topbar">
        <div className="bts-admin__page-title">👥 ผู้เล่นทั้งหมด</div>
        <div className="bts-admin__actions">
          <button className="bts-admin__btn bts-admin__btn--dark" onClick={fetch} disabled={loading}>{loading ? 'โหลด...' : 'รีเฟรช'}</button>
          {canManage && players && <button className="bts-admin__btn bts-admin__btn--green bts-admin__btn--sm" onClick={() => {
            downloadCSV(['สถานะ','ชื่อ','BigPoint','ทีม','คะแนน','เจ้าของ','บิลสำเร็จ','บิลรอคิว','เข้าร่วม'],
              allPlayers.map(p=>[
                p.playerType==='inTeam'?'คะแนนสะสมสำเร็จ':
                p.playerType==='inTeamNoScan'?'ในทีม ยังไม่สแกน':
                p.playerType==='receiptOnly'?'สแกนแล้ว (ไม่มีทีม)':
                p.playerType==='waiting'?'รอสร้างทีม':
                p.playerType==='registerOnly'?'ผูก BigPoint':'-',
                p.display_name||'',p.bigpoint_id||'',p.party_name||'-',p.personal_tickets||0,p.is_owner?'ใช่':'',p.receiptCount||0,p.pendingCount||0,p.joined_at?new Date(p.joined_at).toLocaleString('th-TH'):'-'
              ]),
              'bts_players.csv');
          }}>Export CSV</button>}
        </div>
      </div>
      {loading && <p>กำลังโหลด...</p>}
      {summary && (<>
        <div className="bts-admin__cards">
          <div className={`bts-admin__card ${filter==='all'?'bts-admin__card--active':''}`} style={{ borderColor:'#333' }} onClick={() => { setFilter('all'); setPage(1); }}><div className="bts-admin__card-value" style={{ color:'#333' }}>{summary.total}</div><div className="bts-admin__card-label">ทั้งหมด</div></div>
          <div className={`bts-admin__card ${filter==='inTeam'?'bts-admin__card--active':''}`} style={{ borderColor:'#10C851' }} onClick={() => { setFilter('inTeam'); setPage(1); }}><div className="bts-admin__card-value" style={{ color:'#10C851' }}>{summary.inTeam}</div><div className="bts-admin__card-label">คะแนนสะสมสำเร็จ</div></div>
          <div className={`bts-admin__card ${filter==='inTeamNoScan'?'bts-admin__card--active':''}`} style={{ borderColor:'#9B59B6' }} onClick={() => { setFilter('inTeamNoScan'); setPage(1); }}><div className="bts-admin__card-value" style={{ color:'#9B59B6' }}>{summary.inTeamNoScan}</div><div className="bts-admin__card-label">เข้าทีมยังไม่สแกน</div></div>
          <div className={`bts-admin__card ${filter==='waiting'?'bts-admin__card--active':''}`} style={{ borderColor:'#FF8A00' }} onClick={() => { setFilter('waiting'); setPage(1); }}><div className="bts-admin__card-value" style={{ color:'#FF8A00' }}>{summary.waiting}</div><div className="bts-admin__card-label">รอสร้างทีม</div></div>
          <div className={`bts-admin__card ${filter==='newMember'?'bts-admin__card--active':''}`} style={{ borderColor:'#459efc' }} onClick={() => { setFilter('newMember'); setPage(1); }}><div className="bts-admin__card-value" style={{ color:'#459efc' }}>{summary.newMember}</div><div className="bts-admin__card-label">New Member (ได้ x2)</div></div>
        </div>
        <div className="bts-admin__case-box">
          <div className="bts-admin__case-title">สรุปกรณี</div>
          <div className="bts-admin__case-row" onClick={() => { setFilter('inTeam'); setPage(1); }}><span>✅ ผู้เล่นในทีม (คะแนนสะสมสำเร็จแล้ว)</span><span className="bts-admin__case-count">{summary.inTeam} คน</span></div>
          <div className="bts-admin__case-row" onClick={() => { setFilter('inTeamNoScan'); setPage(1); }}><span>🕐 เข้าทีมแต่ยังไม่สแกน</span><span className="bts-admin__case-count">{summary.inTeamNoScan} คน</span></div>
          <div className="bts-admin__case-row" onClick={() => { setFilter('waiting'); setPage(1); }}><span>👥 ผู้เล่นรอสร้าง/เข้าร่วมทีม</span><span className="bts-admin__case-count">{summary.waiting} คน</span></div>
          <div className="bts-admin__case-row" onClick={() => { setFilter('newMember'); setPage(1); }}><span>⭐ New Member (ได้ x2 bonus)</span><span className="bts-admin__case-count">{summary.newMember} คน</span></div>
        </div>
        <div className="bts-admin__filter-info"><span>แสดง {filtered.length} คน</span><SearchBox value={search} onChange={v => { setSearch(v); setPage(1); }} /></div>
        <div className="bts-admin__table-wrap">
          <table className="bts-admin__table">
            <thead><tr><th>สถานะ</th><th>ชื่อ</th><th>BigPoint</th><th>ทีม</th><th>คะแนน</th><th>เจ้าของ</th><th>บิลสำเร็จ</th><th>บิลรอคิว</th><th>เข้าร่วม</th></tr></thead>
            <tbody>{paginate(filtered, page).map((p, i) => (
              <tr key={i} className={p.playerType==='waiting'?'bts-admin__row--warn':''}>
                <td>{
                  p.playerType==='inTeam' ? <Badge color="green">คะแนนสะสมสำเร็จ</Badge> :
                  p.playerType==='inTeamNoScan' ? <Badge color="gray">ในทีม ยังไม่สแกน</Badge> :
                  p.playerType==='receiptOnly' ? <Badge color="green">สแกนแล้ว (ไม่มีทีม)</Badge> :
                  p.playerType==='waiting' ? <Badge color="orange">รอสร้างทีม</Badge> :
                  p.playerType==='registerOnly' ? <Badge color="gray">ผูก BigPoint</Badge> :
                  p.playerType==='newMember' ? (
                    p.receiptCount>0 ? <Badge color="green">สแกนสำเร็จ</Badge> :
                    p.pendingCount>0 ? <Badge color="orange">รอคิว</Badge> :
                    p.party_name && p.party_name!=='-' ? <Badge color="green">ในทีม</Badge> :
                    <Badge color="gray">ผูก BigPoint</Badge>
                  ) :
                  <Badge color="gray">-</Badge>
                }</td>
                <td>{p.display_name}</td><td>{p.bigpoint_id||'-'}</td><td>{p.party_name||'-'}</td>
                <td><b>{p.personal_tickets||0}</b></td>
                <td>{p.is_owner?'👑':''}</td>
                <td>{p.receiptCount||0}</td><td>{p.pendingCount||0}</td>
                <td>{p.joined_at?new Date(p.joined_at).toLocaleString('th-TH'):'-'}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
        <Pagination total={filtered.length} page={page} setPage={setPage} />
      </>)}
    </div>
  );
};

export default PlayersTab;
